import { css } from '@emotion/react'
import { Button, Cell, Grid, HFlow, Tooltip, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import { CheckboxField, Form, FormRenderProps, NumberField, SubmitButton } from 'components/form'
import { resolveValue } from 'components/form/final-form/hooks/useField'
import { confirm } from 'components/modals/confirm'
import { FormApi } from 'final-form'
import createDecorator from 'final-form-calculate'
import { TipoAtestadoEnum } from 'graphql/types.generated'
import { Fragment, useState } from 'react'
import { safeDateAsYyyyMmDd } from 'util/date/formatDate'
import { metaPath } from 'util/metaPath'

import { AtestadoAssinaturaDigitalView } from '../assinatura-digital/AtestadoAssinaturaDigitalView'
import { AtestadoConfirmarAssinaturaDigitalModal } from '../assinatura-digital/AtestadoConfirmarAssinaturaDigitalModal'
import { MESSAGE_TOOLTIP_SUBMIT_ATESTADO_DISABLED } from '../assinatura-digital/model-atestadoAssinaturaDigital'
import { useAtestadoAssinaturaDigitalForm } from '../assinatura-digital/useAtestadoAssinaturaDigitalForm'
import AtestadoModel, { CidadaoAtestado } from '../components/model-atestado'
import LicencaMaternidadeTextBox, { getLicencaMaternidadeText } from './LicencaMaternidadeTextBox'
import { licencaMaternidadeFormValidator } from './validator'

export interface LicencaMaternidadeFormProps {
  cidadao: CidadaoAtestado
  dataAtendimento: Instant
  onSubmit(values: AtestadoModel): void
  atendimentoId: ID
  prontuarioId: ID
}

export const name = metaPath<AtestadoModel>()

export const cpfCnsDecorator = createDecorator({
  field: name.naoPossui.absolutePath(),
  updates: {
    [name.cpfCns.absolutePath()]: (value: boolean, allValues: AtestadoModel) => {
      if (value) return (allValues.cpfCns = '')
      else return allValues.cpfCns
    },
  },
})

export default function LicencaMaternidadeForm(props: LicencaMaternidadeFormProps) {
  const { cidadao, dataAtendimento, onSubmit, atendimentoId, prontuarioId } = props
  const possuiCpfCns = cidadao.cpf || cidadao.cns

  const alert = useAlert()
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false)
  const [isEmailCidadaoDirty, setIsEmailCidadaoDirty] = useState(false)
  const [isEmailCidadaoCanceled, setIsEmailCidadaoCanceled] = useState(false)

  const tipoAtestado = TipoAtestadoEnum.LICENCA_MATERNIDADE

  const handleSubmit = (values: AtestadoModel, formApi: FormApi) => {
    onSubmit({ ...values, tipo: tipoAtestado })
    setTimeout(formApi.reset)
    formApi.getRegisteredFields().forEach((item) => formApi.resetFieldState(item))
    alert('success', 'Licença maternidade salva com sucesso.')
  }

  const {
    emailCidadao,
    handleSubmitWrapper,
    isAssinando,
    isModalOpen,
    onConfirm,
    onCancel,
    errorMessage,
    onAlertClose,
  } = useAtestadoAssinaturaDigitalForm({
    cidadaoId: cidadao.id,
    atendimentoId,
    prontuarioId,
    tipoAtestado,
    dataAtendimento,
    onSubmit: handleSubmit,
  })

  const handleCancel = (formProps: FormRenderProps<AtestadoModel>) => {
    if (formProps.dirty || isEmailCidadaoDirty) {
      confirm({
        title: 'Deseja cancelar a inclusão?',
        body: 'As alterações realizadas serão perdidas.',
        cancelLabel: 'Não, continuar inclusão',
        confirmLabel: 'Sim, cancelar inclusão',
        onConfirm: () => {
          alert('success', 'Cadastro de licença maternidade cancelado com sucesso.')
          setIsEmailCidadaoCanceled(true)
          formProps.form.reset()
        },
      })()
    } else {
      alert('success', 'Cadastro de licença maternidade cancelado com sucesso.')
      setIsEmailCidadaoCanceled(true)
    }
    formProps.form.getRegisteredFields().forEach((item) => formProps.form.resetFieldState(item))
  }

  const renderForm = (formProps: FormRenderProps<AtestadoModel>) => {
    const values = resolveValue(formProps.values, name) || ({} as AtestadoModel)
    const dataAtendimentoAsLocalDate = safeDateAsYyyyMmDd(dataAtendimento)
    values.descricao = getLicencaMaternidadeText(cidadao, values.cpfCns, values.naoPossui, dataAtendimentoAsLocalDate)
    values.data = dataAtendimentoAsLocalDate

    return (
      <Fragment>
        {!possuiCpfCns && (
          <Cell size={8}>
            <Grid gap={1}>
              <Cell size={6}>
                <NumberField
                  name={name.cpfCns}
                  label='CPF/CNS'
                  disabled={values.naoPossui}
                  required={!values.naoPossui}
                  maxLength={15}
                />
              </Cell>
              <Cell>
                <CheckboxField name={name.naoPossui} label='Não possui' style={styles.naoPossuiCheckbox} />
              </Cell>
            </Grid>
          </Cell>
        )}
        <AtestadoAssinaturaDigitalView
          name={name.assinadoDigitalmente}
          cidadaoId={cidadao.id}
          cidadaoEmail={emailCidadao}
          onEmailCidadaoChange={setIsSubmitDisabled}
          onDirtyChange={setIsEmailCidadaoDirty}
          style={styles.assinaturaDigital}
          hasCid10={!!formProps.values.cid10}
          emailCanceled={isEmailCidadaoCanceled}
          onEmailCancel={setIsEmailCidadaoCanceled}
        />
        <Cell size={12}>
          <VFlow>
            <LicencaMaternidadeTextBox naoPossui={values.naoPossui} cpfCns={values.cpfCns} {...props} />
            <HFlow justifyContent='flex-end'>
              {!possuiCpfCns && (
                <Button size='small' onClick={() => handleCancel(formProps)}>
                  Cancelar
                </Button>
              )}
              <Tooltip text={isSubmitDisabled && MESSAGE_TOOLTIP_SUBMIT_ATESTADO_DISABLED}>
                <SubmitButton
                  size='small'
                  kind='primary'
                  handleSubmit={formProps.handleSubmit}
                  disabled={isSubmitDisabled || isAssinando}
                  loading={isAssinando}
                >
                  Salvar atestado
                </SubmitButton>
              </Tooltip>
            </HFlow>
          </VFlow>
        </Cell>
        {isModalOpen && (
          <AtestadoConfirmarAssinaturaDigitalModal
            emailCidadao={emailCidadao}
            onConfirm={onConfirm}
            onCancel={onCancel}
            isAssinando={isAssinando}
            errorMessage={errorMessage}
            onAlertClose={onAlertClose}
            tipoAtestado={tipoAtestado}
            descricao={formProps.values.descricao}
          />
        )}
      </Fragment>
    )
  }

  return (
    <Form<AtestadoModel>
      render={renderForm}
      onSubmit={handleSubmitWrapper}
      validate={!possuiCpfCns && licencaMaternidadeFormValidator}
      decorators={[cpfCnsDecorator]}
    />
  )
}

const styles = {
  naoPossuiCheckbox: css`
    margin-top: 1.75rem;
  `,
  assinaturaDigital: css`
    padding-left: 1rem;
  `,
}
